/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is prohibited.
 */

import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import './FeatureUsageStats.css';

const FeatureUsageStats: React.FC = () => {
  const [stats, setStats] = useState<{
    collectionDescriptionUpdateCount: number;
    collectionImageUpdateCount: number;
    lastUpdated: any;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const analyticsDoc = await getDoc(doc(db, 'analytics', 'feature_usage'));
      if (analyticsDoc.exists()) {
        setStats(analyticsDoc.data() as any);
      } else {
        // No data yet — display zeros
        setStats({
          collectionDescriptionUpdateCount: 0,
          collectionImageUpdateCount: 0,
          lastUpdated: null
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return 'Never';
    // Handle Firestore Timestamp
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="feature-stats-card">
        <h3>Feature Usage Stats</h3>
        <div className="loading">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="feature-stats-card">
        <h3>Feature Usage Stats</h3>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="feature-stats-card">
      <div className="stats-header">
        <h3>Feature Usage Stats</h3>
        <button onClick={fetchStats} className="refresh-stats-btn" title="Refresh stats">
          🔄
        </button>
      </div>
      
      <div className="stats-grid">
        <div className="stat-item">
          <div className="stat-label">Collection Descriptions Updated</div>
          <div className="stat-value">{stats?.collectionDescriptionUpdateCount || 0}</div>
        </div>
        
        <div className="stat-item">
          <div className="stat-label">Collection Images Updated</div>
          <div className="stat-value">{stats?.collectionImageUpdateCount || 0}</div>
        </div>
      </div>

      <div className="stats-footer">
        <span className="last-updated">
          Last activity: {formatTimestamp(stats?.lastUpdated)}
        </span>
      </div>

      <div className="stats-note">
        <small>📊 Tracks updates from non-admin users only</small>
      </div>
    </div>
  );
};

export default FeatureUsageStats;